import { SearchActionFilter } from '@enview/interface/types/Bill';
import { t } from 'i18next';
import { isEqual } from 'lodash-es';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import CloseIcon from '../../../components/svg/CloseIcon';
import { g as abilityGlossary } from '../../../config/ability';
import { checkPermission } from '../../../dux';
import {
  chamberOptions,
  legislativeTypeOptions,
} from '../../../helpers/BillSearchHelper';
import DropDownButton from '../../Shared/Buttons/DropDownButton';
import CustomDropdown from '../../Shared/DropDowns/CustomDropdown';
import { OptionType } from '../../Shared/DropDowns/MultipleSelectDropDown';
import MultiSelectForm from '../../Shared/Forms/MultiSelectForm';
import SingleSelectForm from '../../Shared/Forms/SingleSelectForm';
import SwitchIcon from '../../Shared/Icons/SwitchIcon';
import GateTooltip, { GateTooltipFeature } from '../../Shared/Tooltips/TooltipGate';
import BillActionFilterDropdown from './BillActionFilterDropdown';
import BillStatusFilterDropdown from './BillStatusFilterDropdown';
import TagNameFilter from '../../Shared/Search/MultiSelectFilter';

type AdvSearchFiltersProps = {
  clearAllFilters: () => void;
  sessionOptions: OptionType<string>[];
  sessions: string[];
  setSessions: (arg: (string | null)[]) => void;
  chamber: string;
  setChamber: (arg: string) => void;
  status: string[];
  setStatus: (arg: (string | null)[]) => void;
  isIncludeTrackedBillsOn: boolean;
  setIncludeTrackedBills: (bool: boolean) => void;
  isOnlyShowMomentumBillsOn: boolean;
  setOnlyShowMomentumBills: (bool: boolean) => void;
  legislativeType: string[];
  setLegislativeType: (arg: (string | null)[]) => void;
  billAction: SearchActionFilter | undefined;
  setBillAction: (arg: SearchActionFilter | undefined) => void;
  billTopicsSelector: ReactElement | null;
  setSearchChanged: (bool: boolean) => void;
  statusOptions?: OptionType<string>[];
  selectedTags?: string[];
  setSelectedTags?: (tags: string[]) => void;
  selectedTagFilterOption?: string;
  setSelectedTagFilterOption?: (option: string) => void;
  tagOptions?: OptionType<any>[];
};

const AdvSearchFilters = (props: AdvSearchFiltersProps): ReactElement => {
  const {
    clearAllFilters,
    sessionOptions,
    sessions,
    setSessions,
    chamber,
    setChamber,
    status,
    setStatus,
    isIncludeTrackedBillsOn,
    setIncludeTrackedBills,
    isOnlyShowMomentumBillsOn,
    setOnlyShowMomentumBills,
    setSearchChanged,
    legislativeType,
    setLegislativeType,
    billAction,
    setBillAction,
    statusOptions,
    billTopicsSelector,
    selectedTags,
    setSelectedTags,
    selectedTagFilterOption,
    setSelectedTagFilterOption,
    tagOptions,
  } = props;

  const [showSessionFilter, setShowSessionFilter] = useState(false);
  const [showChamberFilter, setShowChamberFilter] = useState(false);
  const [showLegislativeTypeFilter, setShowLegislativeTypeFilter] = useState(false);

  useEffect(() => setSearchChanged(true), [selectedTags, selectedTagFilterOption]);

  const dispatch = useDispatch();
  const clearAllRef = useRef(() => {});

  const canUseTrackedBillsFilter = () => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.BILL_SEARCH_TRACKED_FILTER,
        true,
      ),
    );
  };

  const canUseMomentumFilter = () => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.MOMENTUM, true),
    );
  };

  const handleSessionFiltersChange = (
    updatedSessionFilters: (string | null)[],
  ): void => {
    if (isEqual(updatedSessionFilters, sessions)) return;
    setSessions(updatedSessionFilters);
    setSearchChanged(true);
  };

  const handleChamberFiltersChange = (updatedChamberFilter: string): void => {
    if (isEqual(updatedChamberFilter, chamber)) return;
    setChamber(updatedChamberFilter);
    setSearchChanged(true);
  };

  const handleStatusFiltersChange = (updatedStatusFilters: (string | null)[]): void => {
    if (isEqual(updatedStatusFilters, status)) return;
    setStatus(updatedStatusFilters);
    setSearchChanged(true);
  };

  const handleIncludeTrackedBillsChange = (updatedSelection: boolean): void => {
    setIncludeTrackedBills(updatedSelection);
    setSearchChanged(true);
  };

  const handleOnlyShowMomentumBillsChange = (updatedSelection: boolean): void => {
    setOnlyShowMomentumBills(updatedSelection);
    setSearchChanged(true);
  };

  const handleLegislativeTypeFiltersChange = (
    updatedLegislativeTypeFilters: (string | null)[],
  ): void => {
    if (isEqual(updatedLegislativeTypeFilters, legislativeType)) return;
    setLegislativeType(updatedLegislativeTypeFilters);
    setSearchChanged(true);
  };

  const dropdownTargetButton = (
    isDisplayed: boolean,
    label: string,
    onClick: () => void,
    selectedCount?: number,
    disabled?: boolean,
    customContent?: ReactElement,
    ineligibleJurisdictions?: boolean,
  ): ReactElement => {
    // For the purpose of this component, assuming that filter dropdowns will be disabled for
    // the reason of feature-gating logged-out users
    const id = `${label.replace(/\s/g, '-').toLowerCase()}-filter`;
    return (
      <>
        <DropDownButton
          disabled={ineligibleJurisdictions ?? disabled}
          dropdownDisplayed={isDisplayed}
          id={id}
          label={label}
          onClick={onClick}
          selectedCount={selectedCount}
        />
        {disabled && (
          <GateTooltip
            accountType="pro"
            anchorId={id}
            customContent={customContent}
            place="bottom"
          />
        )}
      </>
    );
  };

  const clearAll = () => {
    clearAllFilters();
    if (clearAllRef.current && typeof clearAllRef.current === 'function') {
      clearAllRef.current();
    }
  };

  const renderMultiSelectOptions = (
    currentSelection: string[] | undefined,
    options: OptionType<string>[],
    updateSelection: (values: string[]) => void,
  ): ReactElement => {
    return (
      <MultiSelectForm
        currentSelection={currentSelection ?? []}
        options={options}
        showClearAll
        showSelectAll
        showSelectButtonsBottom
        singleColumn
        updateSelection={updateSelection}
      />
    );
  };

  const renderSingleSelectOptions = (
    currentSelection: string,
    options: OptionType<string>[],
    updateSelection: (value: string) => void,
  ): ReactElement => {
    return (
      <SingleSelectForm
        currentSelection={currentSelection}
        options={options}
        singleColumn
        updateSelection={updateSelection}
      />
    );
  };

  const chamberCount = chamber !== 'both' ? 1 : undefined;
  return (
    <div className="adv-search-filters">
      <div className="row">
        {/* Session Dropdown */}
        <div className="adv-filter">
          <CustomDropdown
            setShowDropdown={setShowSessionFilter}
            showDropdown={showSessionFilter}
            target={dropdownTargetButton(
              showSessionFilter,
              t('buttons.search.sessions'),
              () => setShowSessionFilter(!showSessionFilter),
              sessions.length || undefined,
            )}
          >
            <div className="adv-search-selector">
              {renderMultiSelectOptions(
                sessions,
                sessionOptions,
                handleSessionFiltersChange,
              )}
            </div>
          </CustomDropdown>
        </div>

        {/* Chamber Dropdown */}
        <div className="adv-filter">
          <CustomDropdown
            setShowDropdown={setShowChamberFilter}
            showDropdown={showChamberFilter}
            target={dropdownTargetButton(
              showChamberFilter,
              t('buttons.search.chambers'),
              () => setShowChamberFilter(!showChamberFilter),
              chamberCount,
            )}
          >
            <div className="adv-search-selector" style={{ width: '180px' }}>
              {renderSingleSelectOptions(
                chamber,
                chamberOptions,
                handleChamberFiltersChange,
              )}
            </div>
          </CustomDropdown>
        </div>

        {/* Bill Status */}
        <BillStatusFilterDropdown
          handleStatusFiltersChange={handleStatusFiltersChange}
          status={status}
          statusOptions={statusOptions}
        />

        {/* Legislative Type */}
        <div className="adv-filter">
          <CustomDropdown
            setShowDropdown={setShowLegislativeTypeFilter}
            showDropdown={showLegislativeTypeFilter}
            target={dropdownTargetButton(
              showLegislativeTypeFilter,
              t('buttons.search.legislativeType'),
              () => setShowLegislativeTypeFilter(!showLegislativeTypeFilter),
              legislativeType.length || undefined,
            )}
          >
            <div className="adv-search-selector">
              {renderMultiSelectOptions(
                legislativeType,
                legislativeTypeOptions,
                handleLegislativeTypeFiltersChange,
              )}
            </div>
          </CustomDropdown>
        </div>

        {/* Bill Actions */}
        <BillActionFilterDropdown
          billAction={billAction}
          setBillAction={setBillAction}
          setSearchChanged={setSearchChanged}
        />

        {/* Bill Topics selector gets passed in and rendered here in a feature-flag-off case */}
        {billTopicsSelector && billTopicsSelector}
        <button
          className="btn btn-text ml-auto p-0"
          id="clear-adv-btn"
          onClick={clearAll}
          type="button"
        >
          <CloseIcon fill="#EB5757" />
          <span>Clear All</span>
        </button>
      </div>

      {/* Tracked Bills toggle */}
      <div className="row">
        <div className="adv-filter">
          {tagOptions && setSelectedTags && (
            <TagNameFilter
              className="tagnames mr-3"
              clearAllRef={clearAllRef}
              options={tagOptions}
              selectedFilterOption={selectedTagFilterOption}
              selectedValues={selectedTags ?? []}
              setSelectedFilterOption={setSelectedTagFilterOption}
              setSelectedValues={setSelectedTags}
              title="Tag Names"
            />
          )}
          <div
            className="d-flex mb-0"
            id="tracked-bills-filter"
            style={{
              alignItems: 'center',
            }}
          >
            <SwitchIcon
              disabled={!canUseTrackedBillsFilter()}
              id="tracked-bills-switch"
              onChange={handleIncludeTrackedBillsChange}
              value={canUseTrackedBillsFilter() && isIncludeTrackedBillsOn}
            />
            <span>{t('buttons.search.includeTracked')}</span>
          </div>
          {!canUseTrackedBillsFilter() && (
            <GateTooltip
              accountType="pro"
              anchorId="tracked-bills-filter"
              customContent={
                <Trans
                  components={{
                    trackLink: (
                      <a href={t('urls.advancedSearchTips')} target="_blank" />
                    ),
                    gate: <p className="tooltip-header" />,
                  }}
                  i18nKey="featureGating.tooltipText.includeTracked"
                  key="featureGating.tooltipText.includeTracked"
                />
              }
              featureName={GateTooltipFeature.TrackedBillsFilter}
              place="bottom"
            />
          )}
          {/* Only Show Momentum Bills */}
          <div
            className="d-flex mb-0 ml-3"
            id="momentum-bills-tooltip"
            style={{
              alignItems: 'center',
            }}
          >
            <SwitchIcon
              disabled={!canUseMomentumFilter()}
              id="momentum-bills-switch"
              onChange={handleOnlyShowMomentumBillsChange}
              value={isOnlyShowMomentumBillsOn}
            />
            <span>{t('buttons.search.showOnlyMomentum')}</span>
            {canUseMomentumFilter() ? (
              <ReactTooltip
                anchorId="momentum-bills-tooltip"
                className="tooltip-content"
                clickable
                delayHide={500}
                place="bottom"
                style={{ maxWidth: '300px' }}
              >
                <Trans
                  components={{
                    momentumLink: (
                      <a href={t('urls.momentumInfoLink')} target="_blank" />
                    ),
                  }}
                  i18nKey="bill.search.dropdownTooltipText.momentum"
                />
              </ReactTooltip>
            ) : (
              <GateTooltip
                accountType="pro"
                anchorId="momentum-bills-tooltip"
                customContent={
                  <Trans
                    components={{
                      aiLink: <a href={t('urls.momentumInfoLink')} target="_blank" />,
                      gate: <p className="tooltip-header" />,
                    }}
                    i18nKey="featureGating.tooltipText.momentum"
                    key="featureGating.tooltipText.momentum"
                  />
                }
                featureName={GateTooltipFeature.MomentumFilter}
                place="bottom"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvSearchFilters;
